import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SPORTS, US_STATES } from '../utils/constants';
import './SignUpPage.css';

const FOCUS_AREAS = [
  'Offense', 'Defense', 'Footwork', 'Conditioning / Fitness',
  'Confidence Building', 'Teamwork / Communication',
  'Game IQ / Strategy', 'Speed & Agility', 'Fundamentals / Basics',
];

const SPORT_OPTIONS = [
  { label: '🏀 Basketball',      value: 'Basketball'       },
  { label: '🏈 Football',        value: 'Football'         },
  { label: '⚽ Soccer',          value: 'Soccer'           },
  { label: '⚾ Baseball/Softball', value: 'Baseball/Softball' },
  { label: '🎾 Tennis',          value: 'Tennis'           },
  { label: '🏐 Volleyball',      value: 'Volleyball'       },
  { label: '🤼 Wrestling',       value: 'Wrestling'        },
  { label: '⛳ Golf',            value: 'Golf'             },
];

function TagPicker({ options, selected, onChange, max }) {
  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      if (max && selected.length >= max) return;
      onChange([...selected, value]);
    }
  };

  return (
    <div className="tag-picker">
      {options.map((opt) => {
        const value = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        const isSelected = selected.includes(value);
        return (
          <button
            key={value}
            type="button"
            className={`tag-option${isSelected ? ' selected' : ''}`}
            onClick={() => toggle(value)}
          >
            {isSelected && <span className="tag-check">✓</span>}
            {label}
          </button>
        );
      })}
    </div>
  );
}

function SignUpPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const roleParam = params.get('role');
  const redirectParam = params.get('redirect');
  const [role, setRole] = useState(roleParam === 'trainer' ? 'mentor' : 'parent');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    sport: '',
    city: '',
    state: '',
    bio: '',
    hourlyRate: '',
    gradYear: '',
  });
  const [secondarySport, setSecondarySport] = useState('');
  const [mentorFocusTags, setMentorFocusTags] = useState([]);
  const [childSportTags, setChildSportTags] = useState([]);
  const [childFocusTags, setChildFocusTags] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToSchoolAthletics, setAgreedToSchoolAthletics] = useState(false);
  const [agreedToParentPermission, setAgreedToParentPermission] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.fullName.trim()) errs.fullName = 'Full name is required.';
    if (!formData.email.trim()) { errs.email = 'Email is required.'; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Enter a valid email address.';
    if (!formData.password) { errs.password = 'Password is required.'; }
    else if (formData.password.length < 8) errs.password = 'Password must be at least 8 characters.';
    if (formData.password !== formData.confirmPassword) errs.confirmPassword = 'Passwords do not match.';
    if (role === 'mentor') {
      if (!formData.sport) errs.sport = 'Please select your primary sport.';
      if (secondarySport && secondarySport === formData.sport) errs.secondarySport = 'Secondary sport must be different from your primary sport.';
      if (formData.hourlyRate && isNaN(parseFloat(formData.hourlyRate))) errs.hourlyRate = 'Enter a valid number.';
    }
    if (!formData.city.trim()) errs.city = 'City is required.';
    if (!formData.state) errs.state = 'State is required.';
    if (!agreedToTerms) errs.terms = 'You must agree to the Terms of Service.';
    if (role === 'mentor') {
      if (!agreedToSchoolAthletics) errs.schoolAthletics = 'You must acknowledge this before creating a Trainer account.';
      if (!agreedToParentPermission) errs.parentPermission = 'You must acknowledge this before creating a Trainer account.';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      await signUp({
        email: formData.email, password: formData.password, fullName: formData.fullName, role,
        sport: role === 'mentor' ? formData.sport : null,
        secondarySport: role === 'mentor' ? secondarySport || null : null,
        city: formData.city || null, state: formData.state || null,
        bio: formData.bio || null,
        hourlyRate: role === 'mentor' ? formData.hourlyRate : null,
        focusAreas: role === 'mentor' ? mentorFocusTags : [],
        sportsInterest: role === 'parent' ? childSportTags : null,
        focusAreasInterest: role === 'parent' ? childFocusTags : null,
        gradYear: formData.gradYear,
      });

      // Fire welcome email — don't block navigation on email result
      fetch('/.netlify/functions/welcome-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          firstName: formData.fullName.trim().split(' ')[0],
          role,
        }),
      }).catch(() => {});

      navigate(redirectParam || '/profile');
    } catch (err) {
      console.error('[SignUpPage] signUp error:', err);
      setSubmitError(err.message || err.details || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card card">
        <div className="auth-header">
          <h1 className="auth-title">Create Your Account</h1>
          <p className="auth-sub">Join the GamePlannr community today.</p>
        </div>
        <div className="role-toggle">
          <button type="button" className={`role-btn${role === 'parent' ? ' active' : ''}`} onClick={() => setRole('parent')}>I'm a Parent</button>
          <button type="button" className={`role-btn${role === 'mentor' ? ' active' : ''}`} onClick={() => setRole('mentor')}>I'm a Trainer</button>
        </div>
        {submitError && <div className="alert alert-error">{submitError}</div>}
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="fullName">Full Name</label>
            <input id="fullName" name="fullName" type="text" className={`form-input${errors.fullName ? ' error' : ''}`} value={formData.fullName} onChange={handleChange} autoComplete="name" placeholder="Your full name" />
            {errors.fullName && <span className="form-error">{errors.fullName}</span>}
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="email">Email Address</label>
            <input id="email" name="email" type="email" className={`form-input${errors.email ? ' error' : ''}`} value={formData.email} onChange={handleChange} autoComplete="email" placeholder="you@example.com" />
            {errors.email && <span className="form-error">{errors.email}</span>}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="password">Password</label>
              <input id="password" name="password" type="password" className={`form-input${errors.password ? ' error' : ''}`} value={formData.password} onChange={handleChange} autoComplete="new-password" placeholder="Min. 8 characters" />
              {errors.password && <span className="form-error">{errors.password}</span>}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="confirmPassword">Confirm Password</label>
              <input id="confirmPassword" name="confirmPassword" type="password" className={`form-input${errors.confirmPassword ? ' error' : ''}`} value={formData.confirmPassword} onChange={handleChange} autoComplete="new-password" placeholder="Repeat your password" />
              {errors.confirmPassword && <span className="form-error">{errors.confirmPassword}</span>}
            </div>
          </div>

          {role === 'mentor' && (
            <>
              {/* Primary + Secondary sport */}
              <div className="form-row form-row-bottom">
                <div className="form-group">
                  <label className="form-label" htmlFor="sport">Primary Sport</label>
                  <select
                    id="sport"
                    name="sport"
                    className={`form-select${errors.sport ? ' error' : ''}`}
                    value={formData.sport}
                    onChange={handleChange}
                  >
                    <option value="">Select your sport</option>
                    {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.sport && <span className="form-error">{errors.sport}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="secondarySport">
                    Secondary Sport (Optional)
                  </label>
                  <select
                    id="secondarySport"
                    className="form-select"
                    value={secondarySport}
                    onChange={(e) => setSecondarySport(e.target.value)}
                  >
                    <option value="">None</option>
                    {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* Grad Year */}
              <div className="form-group form-group-half">
                <label className="form-label" htmlFor="gradYear">Graduation Year</label>
                <input
                  id="gradYear"
                  name="gradYear"
                  type="number"
                  min={2025}
                  max={2032}
                  className="form-input"
                  value={formData.gradYear}
                  onChange={handleChange}
                  placeholder="e.g. 2026"
                />
              </div>

              {/* Session rate */}
              <div className="form-group form-group-half">
                <label className="form-label" htmlFor="hourlyRate">Hourly Rate</label>
                <input
                  id="hourlyRate"
                  name="hourlyRate"
                  type="number"
                  min="0"
                  step="0.01"
                  className={`form-input${errors.hourlyRate ? ' error' : ''}`}
                  value={formData.hourlyRate}
                  onChange={handleChange}
                  placeholder="e.g. 25"
                />
                <span className="form-hint">Collected in person from the parent at your session.</span>
                {errors.hourlyRate && <span className="form-error">{errors.hourlyRate}</span>}
              </div>

              {/* Focus area tag picker */}
              <div className="form-group">
                <label className="form-label">What areas do you feel confident teaching?</label>
                <p className="tag-picker-desc">
                  These appear as tags on your profile — parents can see exactly what you specialise in.
                </p>
                <TagPicker
                  options={FOCUS_AREAS}
                  selected={mentorFocusTags}
                  onChange={setMentorFocusTags}
                  max={5}
                />
                {mentorFocusTags.length > 0 && (
                  <span className="tag-hint">
                    {mentorFocusTags.length} of 5 selected
                  </span>
                )}
              </div>

              {/* Bio */}
              <div className="form-group">
                <label className="form-label" htmlFor="bio">Bio (Optional)</label>
                <textarea
                  id="bio"
                  name="bio"
                  className="form-textarea"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Tell parents a little about yourself and your experience."
                />
              </div>

              <div className="signup-section-divider"><span>Your Location</span></div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="city">City <span style={{ color: '#D32F2F' }}>*</span></label>
                  <input id="city" name="city" type="text" className={`form-input${errors.city ? ' error' : ''}`} value={formData.city} onChange={handleChange} placeholder="e.g. Austin" />
                  {errors.city && <span className="form-error">{errors.city}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="state">State <span style={{ color: '#D32F2F' }}>*</span></label>
                  <select id="state" name="state" className={`form-select${errors.state ? ' error' : ''}`} value={formData.state} onChange={handleChange}>
                    <option value="">Select state</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.state && <span className="form-error">{errors.state}</span>}
                </div>
              </div>
            </>
          )}

          {role === 'parent' && (
            <>
              <div className="card-divider" />
              <span className="section-label">Your Child's Interests (Optional)</span>

              <div className="form-group">
                <label className="form-label">What sports is your child interested in?</label>
                <p className="tag-picker-desc">
                  We'll use this to suggest the best trainers for your child.
                </p>
                <TagPicker
                  options={SPORT_OPTIONS}
                  selected={childSportTags}
                  onChange={setChildSportTags}
                />
              </div>

              <div className="form-group">
                <label className="form-label">What areas would you like your child to improve in?</label>
                <p className="tag-picker-desc">
                  Helps us match you with trainers who specialise in these areas.
                </p>
                <TagPicker
                  options={FOCUS_AREAS}
                  selected={childFocusTags}
                  onChange={setChildFocusTags}
                />
              </div>

              <div className="signup-section-divider"><span>Your Location</span></div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="city">City <span style={{ color: '#D32F2F' }}>*</span></label>
                  <input id="city" name="city" type="text" className={`form-input${errors.city ? ' error' : ''}`} value={formData.city} onChange={handleChange} placeholder="e.g. Austin" />
                  {errors.city && <span className="form-error">{errors.city}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="state">State <span style={{ color: '#D32F2F' }}>*</span></label>
                  <select id="state" name="state" className={`form-select${errors.state ? ' error' : ''}`} value={formData.state} onChange={handleChange}>
                    <option value="">Select state</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.state && <span className="form-error">{errors.state}</span>}
                </div>
              </div>
            </>
          )}

          <div className="form-group terms-group">
            <label className="checkbox-label">
              <input type="checkbox" className="checkbox-input" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} />
              <span className="checkbox-text">I agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link></span>
            </label>
            {errors.terms && <span className="form-error">{errors.terms}</span>}
          </div>

          {role === 'mentor' && (
            <>
              <div className="form-group terms-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={agreedToSchoolAthletics}
                    onChange={(e) => setAgreedToSchoolAthletics(e.target.checked)}
                  />
                  <span className="checkbox-text">
                    I understand that if I participate in school athletics, I am responsible for complying with
                    any rules or disclosure requirements that apply to compensated activities, including MHSAA
                    Personal Branding Activity rules when applicable.
                  </span>
                </label>
                {errors.schoolAthletics && <span className="form-error">{errors.schoolAthletics}</span>}
              </div>

              <div className="form-group terms-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={agreedToParentPermission}
                    onChange={(e) => setAgreedToParentPermission(e.target.checked)}
                  />
                  <span className="checkbox-text">
                    If I am under 18, I confirm that I have permission from my parent or legal guardian to create
                    a GamePlannr account and participate as a Trainer.
                  </span>
                </label>
                {errors.parentPermission && <span className="form-error">{errors.parentPermission}</span>}
              </div>
            </>
          )}

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>{loading ? 'Creating Account…' : 'Create Account'}</button>
        </form>
        <p className="auth-switch">Already have an account? <Link to="/signin">Sign In</Link></p>
      </div>
    </div>
  );
}

export default SignUpPage;
